import localStorageManager from '@/shared/stores/localStorageManager';

interface EnvConfig {
	WEBURL: string;
	ENV_PROD?: boolean;
	ENV_STAGE?: boolean;
	ENV_DEV?: boolean;
	DEBUG: boolean;
	MAX_UPLOAD_SIZE_IN_MB: number;
	user: UserEnvConfig;
}

interface UserEnvConfig {
	ID: number | false;
	CID: string | false;
	CURRENT_SALES_PLACE_ID: string | null;
	LANGUE: string;
	DR: number[];
	ROLES: string[];
	TRANSLATOR_MODE: boolean;
	ENSEIGNE: ClientEnvConfig | false;
}

interface ClientEnvConfig {
	enseigneLibelle: string;
	exitURL: string;
	prefixe: string;
	allowFastReconnect: boolean;
}

class User {
	private readonly data: Readonly<Partial<UserEnvConfig>>;

	constructor(data: Partial<UserEnvConfig> = {}) {
		this.data = Object.freeze({ ...data });
	}

	public getId(): number | null {
		return this.data.ID ? this.data.ID : null;
	}

	public getCid(): string | null {
		return this.data.CID ? this.data.CID : null;
	}

	public getClient(): ClientEnvConfig | null {
		return this.data.ENSEIGNE ? this.data.ENSEIGNE : null;
	}

	public getCurrentSalesPlaceId(): string | null {
		return this.data.CURRENT_SALES_PLACE_ID ? this.data.CURRENT_SALES_PLACE_ID : null;
	}

	public getLanguage(): string {
		return this.data.LANGUE ?? 'fr';
	}

	public hasTranslatorMode(): boolean {
		return this.data.TRANSLATOR_MODE === true;
	}

	public getRoles(): string[] {
		return this.data.ROLES ?? [];
	}

	public getRights(): number[] {
		return this.data.DR ?? [];
	}

	public hasRole(role: string): boolean {
		return this.getRoles().includes(role);
	}

	public hasAnyRole(roles: string[]): boolean {
		return roles.some((role) => this.getRoles().includes(role));
	}

	public hasAllRoles(roles: string[]): boolean {
		return roles.every((role) => this.getRoles().includes(role));
	}

	public hasRight(right: number): boolean {
		return this.getRights().includes(right);
	}

	public hasAnyRight(rights: number[]): boolean {
		return rights.some((right) => this.getRights().includes(right));
	}

	public hasAllRights(rights: number[]): boolean {
		return rights.every((right) => this.getRights().includes(right));
	}

	public getPrimaryRole(): string {
		const roleBeforeTakeControl = localStorageManager.get<string>('roleBeforeTakeControl');
		if (roleBeforeTakeControl) {
			return roleBeforeTakeControl;
		}

		if (this.hasRole('interne')) {
			return 'interne';
		}

		if (this.hasRole('fournisseur')) {
			return 'fournisseur';
		}

		return 'client';
	}
}

class EnvStore {
	private data: Partial<EnvConfig> = {};
	private user: User;

	public constructor() {
		this.user = new User({});
		this.refresh();
	}

	public refresh(): void {
		let parsedData: Partial<EnvConfig> = {};

		if (typeof window !== 'undefined') {
			const rawData = localStorage.getItem('env');
			if (rawData) {
				try {
					parsedData = JSON.parse(rawData) as Partial<EnvConfig>;
				} catch {
					console.warn('Failed to parse env configuration from localStorage.');
				}
			}
		}

		this.data = Object.freeze(parsedData);

		this.user = new User(this.data.user ?? {});
	}

	public getUser(): User {
		return this.user;
	}

	public isProdEnvironment(): boolean {
		return this.data.ENV_PROD === true;
	}

	public isStageEnvironment(): boolean {
		return this.data.ENV_STAGE === true;
	}

	public isDevEnvironment(): boolean {
		return this.data.ENV_DEV === true;
	}

	public getUploadSizeLimit(): number {
		return this.data.MAX_UPLOAD_SIZE_IN_MB ?? 0;
	}
}

const envStore = new EnvStore();
export default envStore;
